import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, UserCircle } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { TrustedDevicesCard } from "@/components/TrustedDevicesCard";
import { FollowDigestCard } from "@/components/investor/FollowDigestCard";
import { InvestorDigestCard } from "@/components/investor/InvestorDigestCard";

export default function Account() {
  const { user } = useAuth();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update password"); }
    finally { setSaving(false); }
  };

  return (
    <AppShell title="Account" subtitle="Manage your sign-in details">
      <div className="max-w-2xl space-y-6">
        <Card className="rounded-xl border bg-card p-6" data-testid="account-profile-card">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <UserCircle className="h-6 w-6" />
            </div>
            <div>
              <div className="h-display text-base font-semibold text-foreground" data-testid="account-name">{user?.name}</div>
              <div className="text-sm text-muted-foreground" data-testid="account-email">{user?.email}</div>
            </div>
          </div>
        </Card>

        <Card className="rounded-xl border bg-card p-6" data-testid="password-card">
          <div className="mb-4 flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <div>
              <h2 className="h-display text-lg font-semibold text-foreground">Password</h2>
              <p className="text-xs text-muted-foreground">Change the password you use to sign in.</p>
            </div>
          </div>
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <Label htmlFor="curpw">Current password</Label>
              <Input id="curpw" type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} required className="mt-1" data-testid="current-password" />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="newpw">New password</Label>
                <Input id="newpw" type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} required minLength={8} placeholder="At least 8 characters" className="mt-1" data-testid="new-password" />
              </div>
              <div>
                <Label htmlFor="confpw">Confirm new password</Label>
                <Input id="confpw" type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} required minLength={8} className="mt-1" data-testid="confirm-password" />
              </div>
            </div>
            <Button type="submit" disabled={saving} className="gap-2" data-testid="change-password-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password
            </Button>
          </form>
        </Card>

        <InvestorDigestCard />

        <FollowDigestCard />

        <TrustedDevicesCard />
      </div>
    </AppShell>
  );
}
